import { z } from 'zod';
import { slugSchema } from './catalog.js';

export const knowledgeVisibilitySchema = z.enum(['PUBLIC', 'INTERNAL']);
export type KnowledgeVisibility = z.infer<typeof knowledgeVisibilitySchema>;

export const knowledgeStatusSchema = z.enum(['DRAFT', 'PUBLISHED']);
export type KnowledgeStatus = z.infer<typeof knowledgeStatusSchema>;

/** Jawaban dipakai utuh di prompt Imma; batas ini menjaga satu entri tidak membengkakkan prompt. */
export const KNOWLEDGE_ANSWER_MAX = 4000;

export const knowledgeIdParamsSchema = z.object({
  id: z.string().min(1).max(64),
});
export type KnowledgeIdParams = z.infer<typeof knowledgeIdParamsSchema>;

// ------------------------------------------------------------
// Kategori
// ------------------------------------------------------------

/**
 * Field mentah tanpa default. Schema ubah dibuat dari sini dengan .partial();
 * .partial() tidak membatalkan .default(), jadi default hanya dipasang di
 * schema buat (lihat catatan yang sama di schemas/article.ts).
 */
const categoryFields = {
  slug: slugSchema,
  name: z.string().trim().min(2).max(80),
  description: z.string().trim().max(300).nullable(),
  sortOrder: z.number().int().min(0).max(10_000),
  isActive: z.boolean(),
};

export const knowledgeCategoryInputSchema = z.object({
  ...categoryFields,
  description: categoryFields.description.default(null),
  sortOrder: categoryFields.sortOrder.default(0),
  isActive: categoryFields.isActive.default(true),
});
export type KnowledgeCategoryInput = z.infer<typeof knowledgeCategoryInputSchema>;

export const knowledgeCategoryUpdateSchema = z
  .object(categoryFields)
  .partial()
  .refine((value) => Object.keys(value).length > 0, 'Tidak ada perubahan yang dikirim');
export type KnowledgeCategoryUpdate = z.infer<typeof knowledgeCategoryUpdateSchema>;

// ------------------------------------------------------------
// Entri
// ------------------------------------------------------------

const entryFields = {
  slug: slugSchema,
  categoryId: z.string().min(1).max(64),
  question: z.string().trim().min(5).max(200),
  answer: z.string().trim().min(10).max(KNOWLEDGE_ANSWER_MAX),
  visibility: knowledgeVisibilitySchema,
  status: knowledgeStatusSchema,
  sortOrder: z.number().int().min(0).max(10_000),
};

export const knowledgeEntryInputSchema = z.object({
  ...entryFields,
  visibility: entryFields.visibility.default('PUBLIC'),
  status: entryFields.status.default('DRAFT'),
  sortOrder: entryFields.sortOrder.default(0),
});
export type KnowledgeEntryInput = z.infer<typeof knowledgeEntryInputSchema>;

export const knowledgeEntryUpdateSchema = z
  .object(entryFields)
  .partial()
  .refine((value) => Object.keys(value).length > 0, 'Tidak ada perubahan yang dikirim');
export type KnowledgeEntryUpdate = z.infer<typeof knowledgeEntryUpdateSchema>;

export const adminKnowledgeQuerySchema = z.object({
  categoryId: z.string().min(1).max(64).optional(),
  visibility: knowledgeVisibilitySchema.optional(),
  status: knowledgeStatusSchema.optional(),
  q: z.string().trim().max(120).optional(),
});
export type AdminKnowledgeQuery = z.infer<typeof adminKnowledgeQuerySchema>;

// ------------------------------------------------------------
// Bentuk respons
// ------------------------------------------------------------

export interface KnowledgeCategoryDto {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  entryCount: number;
  publishedCount: number;
}

export interface KnowledgeEntryDto {
  id: string;
  slug: string;
  categoryId: string;
  categoryName: string;
  question: string;
  answer: string;
  visibility: KnowledgeVisibility;
  status: KnowledgeStatus;
  sortOrder: number;
  updatedAt: string;
  updatedBy: string | null;
}

/** /api/knowledge: hanya entri PUBLIC + PUBLISHED di kategori aktif. */
export interface PublicKnowledgeGroup {
  slug: string;
  name: string;
  description: string | null;
  entries: { slug: string; question: string; answer: string }[];
}
